import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const productLinks = [
  { href: "/canvas-category-landing", label: "Canvas Prints" },
  { href: "/wall-murals-landing", label: "Wall Murals" },
  { href: "/stickers-labels", label: "Stickers & Labels" },
  { href: "/banners", label: "Banners" },
  { href: "/floor-graphics", label: "Floor Graphics" },
  { href: "/vehicle-graphics", label: "Vehicle Graphics" },
  { href: "/custom-order-landing", label: "Custom Orders" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return undefined;
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        isHome && !scrolled && !open
          ? "bg-transparent border-b border-transparent"
          : "border-b border-white/[0.06] bg-[#0A0D14]/82 shadow-[0_18px_40px_rgba(5,6,10,0.2)] backdrop-blur-xl"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="shrink-0 hover:opacity-80 transition-opacity duration-300"
          data-testid="brand-logo"
        >
          <img
            src="/logo.png"
            alt="AM Signs & Prints"
            className="h-10 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {productLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-[13px] tracking-wide font-body transition-colors duration-300 ${
                location.pathname === link.href
                  ? "text-brand-gold"
                  : "text-brand-text-secondary hover:text-brand-text"
              }`}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/request-quote"
            className="hidden md:inline-flex bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-6 py-2.5 text-[13px] font-medium tracking-wide"
            data-testid="header-request-quote-button"
          >
            Request Quote
          </Link>
          <button
            className="md:hidden text-brand-text p-1"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden bg-brand-surface/95 backdrop-blur-lg border-t border-brand-border px-6 py-5"
          data-testid="mobile-menu"
        >
          {productLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-sm text-brand-text-secondary hover:text-brand-text transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/request-quote"
            onClick={() => setOpen(false)}
            className="block mt-3 py-3 text-sm text-brand-gold font-medium"
          >
            Request Quote
          </Link>
        </div>
      )}
    </header>
  );
}
